import React from "react";
import { PenLine } from "lucide-react";
import { supabase } from "../lib/supabase";
import { CompletitudDeMarca, type PasoDeMarca } from "./CompletitudDeMarca";
import { ConexionesDeMarca } from "./ResumenDeConexiones";

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Lo que puede faltar en "Completá tu marca" y abre su "Agregar". */
export type PasoFaltante = "logo" | "colores" | "tipografia" | "memoria" | "producto" | "modelo";

/** `#abc` → `#aabbcc`, para poder sumarle un canal alfa como sufijo. */
const hex6 = (hex: string): string => {
  const h = hex.replace("#", "");
  return `#${h.length === 3 ? h.split("").map((c) => c + c).join("") : h}`;
};

type DatosDeMarca = {
  paleta: string[];
  tieneTipografia: boolean;
  tieneMemoria: boolean;
  productos: number;
  modelos: number;
};

/**
 * Lo que la cabecera muestra o cuenta de la marca activa y que `useBrandProfile` no
 * trae: la paleta (`brand_profiles.colors`) y lo necesario para "Completá tu marca"
 * (tipografía, memoria, cuántos productos y modelos propios hay) —
 * ADR-2026-10-02-mi-marca-completitud.
 *
 * Se relee con `atlas:brand-changed`, que `CompanyData` emite al guardar nombre, rubro,
 * logo, colores, tipografía o memoria, y cuando cambia `version` (el tab: al volver de
 * Productos o Modelos puede haber uno nuevo). Los conteos son `head` (sin filas).
 * Mientras no llegó la primera lectura devuelve `null`, para no pintar un porcentaje
 * equivocado.
 *
 * Tres resguardos — ADR-2026-10-05-mi-marca-datos-de-la-cabecera-atados-a-la-marca:
 *  • Los datos se guardan con la marca de la que vinieron y sólo se devuelven para
 *    esa: al cambiar de marca no se ve la paleta ni el progreso de la anterior.
 *  • Gana la última lectura: dos `atlas:brand-changed` seguidos disparan dos, y la
 *    vieja puede volver después.
 *  • Si alguna consulta falla, se quedan los últimos valores conocidos: un conteo
 *    fallido no es "0 productos" ni una lectura fallida es "sin colores", y ofrecer
 *    "Agregar" lo que ya está cargado es peor que no actualizar.
 */
const useDatosDeMarca = (
  companyId: string | null,
  brandId: string | null,
  version: string,
): DatosDeMarca | null => {
  const clave = `${companyId}:${brandId}`;
  const [leidos, setLeidos] = React.useState<{ clave: string; datos: DatosDeMarca } | null>(
    null,
  );

  React.useEffect(() => {
    let cancelado = false;
    let ultima = 0;
    const leer = async () => {
      if (!companyId || !brandId) return;
      const esta = ++ultima;
      const [marca, config, productos, modelos] = await Promise.all([
        supabase
          .from("brand_profiles")
          .select("colors, fonts, context")
          .eq("id", brandId)
          .maybeSingle(),
        // La memoria vieja de la marca por defecto vive acá; `CompanyData` la muestra
        // si la marca no tiene `context` propio, así que cuenta igual.
        supabase
          .from("config")
          .select("media_agent_analysis")
          .eq("id_company", companyId)
          .maybeSingle(),
        // Mismo criterio que la grilla (`fetchUserProducts`): los de esta marca y los
        // que no tienen marca.
        supabase
          .from("variantes")
          .select("id, productos!inner(brand_id)", { count: "exact", head: true })
          .eq("activo", true)
          .eq("id_company", companyId)
          .or(`brand_id.eq.${brandId},brand_id.is.null`, { referencedTable: "productos" }),
        // Sólo los propios: los de Atlas (`id_company` nulo) los tiene todo el mundo.
        supabase
          .from("modelos")
          .select("id", { count: "exact", head: true })
          .eq("id_company", companyId),
      ]);
      if (cancelado || esta !== ultima) return;
      if (marca.error || config.error || productos.error || modelos.error) return;
      const colores = Array.isArray(marca.data?.colors)
        ? (marca.data.colors as { hex?: string }[])
        : [];
      const fuentes = (marca.data?.fonts as { suggested?: unknown } | null)?.suggested;
      setLeidos({
        clave: `${companyId}:${brandId}`,
        datos: {
        paleta: colores.map((c) => (c?.hex || "").trim()).filter((h) => HEX.test(h)),
        tieneTipografia: Array.isArray(fuentes) && fuentes.length > 0,
        tieneMemoria: !!(
          (marca.data?.context as string | null)?.trim() ||
          (config.data?.media_agent_analysis as string | null)?.trim()
        ),
        productos: productos.count ?? 0,
        modelos: modelos.count ?? 0,
        },
      });
    };
    void leer();
    window.addEventListener("atlas:brand-changed", leer);
    return () => {
      cancelado = true;
      window.removeEventListener("atlas:brand-changed", leer);
    };
  }, [companyId, brandId, version]);

  return leidos?.clave === clave ? leidos.datos : null;
};

/**
 * Los colores del halo de la cabecera: los tres más saturados de la paleta, sin los
 * casi negros ni casi blancos (un halo gris no dice nada de la marca). Sin ninguno que
 * sirva, los azules de Atlas.
 */
const coloresDelHalo = (paleta: string[]): [string, string, string] => {
  const utiles = paleta
    .map((hex) => {
      const h = hex6(hex);
      const [r, g, b] = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const l = (max + min) / 2;
      const sat = max === min ? 0 : (max - min) / (1 - Math.abs(2 * l - 1));
      return { h, sat, l };
    })
    .filter((c) => c.sat > 0.2 && c.l > 0.1 && c.l < 0.92)
    .sort((a, b) => b.sat - a.sat)
    .map((c) => c.h);
  if (utiles.length === 0) return ["#3D5CFF", "#8EA2FF", "#C9D3FF"];
  return [utiles[0]!, utiles[1] ?? utiles[0]!, utiles[2] ?? utiles[1] ?? utiles[0]!];
};

const MASCARA_DEL_HALO =
  "linear-gradient(to bottom, black 35%, transparent), linear-gradient(to right, transparent, black 14%, black 86%, transparent)";
/**
 * Abajo de `lg` arriba de la cabecera está el título "Marca" de `MobilePageTitle`, no
 * el borde de la página: el halo arranca en la cabecera y entra en degradé también
 * desde arriba. Empezando 24px más arriba y a pleno, tapaba el título y lo cortaba
 * con una línea recta — ADR-2026-10-05-mi-marca-cabecera-en-mobile.
 */
const MASCARA_DEL_HALO_MOBILE =
  "linear-gradient(to bottom, transparent, black 30%, black 45%, transparent), linear-gradient(to right, transparent, black 14%, black 86%, transparent)";

/** Hasta dos iniciales del nombre, para cuando la marca no tiene logo. */
const iniciales = (nombre: string): string =>
  nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("") || "·";

/**
 * Cabecera de identidad de Mi marca —
 * ADR-2026-10-02-mi-marca-cabecera-de-identidad; en todos los anchos desde
 * ADR-2026-10-05-mi-marca-cabecera-en-mobile.
 *
 * Abajo de `lg` es la misma cabecera en chico: logo de 56px, el nombre (que abre el
 * cambio de marca: ahí no está la tarjeta de la sidebar), la pill de "Completá tu
 * marca" a todo el ancho y los chips debajo. Reemplaza a la tarjeta del selector de
 * marca y a las filas de integración que había en mobile.
 *
 * La pantalla abría con un "Marca" genérico y una columna de 440px con cuatro botones
 * apilados; la marca en sí —su logo, su nombre, sus colores— no aparecía en ningún
 * lado salvo adentro de "Información". Ahora es lo primero que se ve, y las
 * integraciones (`children`, los chips) van debajo: son conexiones DE esta marca.
 *
 * El nombre es el `<h1>` de la sección, así que hereda los 32px de `.shell-seccion h1`
 * y abre a la misma altura que el título de las demás secciones.
 *
 * ⚠️ El `<h1>` es hijo DIRECTO de la fila del logo, y la fila es una grilla (logo
 * ocupando dos renglones, nombre y datos en la segunda columna) en vez de un flex con
 * una columna de texto adentro. El shell le suma 40px de `padding-top` al padre directo
 * de cualquier `<h1>` (`.shell-seccion :has(> h1)`, el aire de los títulos de sección):
 * con la columna de texto ese padre era la columna sola, el texto bajaba 40px y el logo
 * quedaba arriba, descentrado. Con la grilla el aire es de la fila entera.
 */
export const CabeceraDeMarca = ({
  companyId,
  brandId,
  nombre,
  rubro,
  logoUrl,
  version,
  palabraModelo,
  onEditar,
  onAgregar,
  escritorio,
  onCambiarMarca,
  children,
}: {
  companyId: string | null;
  brandId: string | null;
  nombre: string;
  rubro: string | null;
  logoUrl: string | null;
  /** Cambia → se vuelven a contar productos y modelos (ver `useDatosDeMarca`). */
  version: string;
  /** "modelo" o "avatar", según el rubro (`palabraDeModelo`). */
  palabraModelo: string;
  onEditar: () => void;
  /**
   * El "Agregar" de cada paso de "Completá tu marca": abre directamente lo que falta
   * (ADR-2026-10-02-mi-marca-agregar-directo). Quién y cómo lo abre lo resuelve la
   * pantalla.
   */
  onAgregar: (que: PasoFaltante) => void;
  /**
   * ≥1024px. Decide la etiqueta del nombre: `<h1>` en el shell de escritorio, donde es
   * el título de la sección; abajo, el título es "Marca" (`MobilePageTitle`) y el
   * nombre va como `<h2>`.
   */
  escritorio: boolean;
  /** Abajo de `lg`, el nombre es el botón para cambiar de marca. */
  onCambiarMarca?: () => void;
  children?: React.ReactNode;
}) => {
  const datos = useDatosDeMarca(companyId, brandId, version);
  /**
   * La cabecera es su propio contexto de apilado (`isolate`, por el halo): el `z-30`
   * del menú de "Completá tu marca" sólo vale ADENTRO de ella, y lo posicionado que
   * viene después en la página —el select de tipografía de Información, por ejemplo—
   * se le pintaba encima. Mientras el menú está abierto, la cabecera entera sube de
   * capa; cerrado no compite con nada.
   */
  const [menuAbierto, setMenuAbierto] = React.useState(false);
  const paleta = datos?.paleta ?? [];
  const [logoRoto, setLogoRoto] = React.useState(false);
  React.useEffect(() => setLogoRoto(false), [logoUrl]);

  const tieneLogo = !!logoUrl && !logoRoto;
  const pasos: PasoDeMarca[] | null = datos && [
    { clave: "logo", etiqueta: "Logo", listo: tieneLogo, ir: () => onAgregar("logo") },
    { clave: "colores", etiqueta: "Paleta de colores", listo: datos.paleta.length > 0, ir: () => onAgregar("colores") },
    { clave: "tipografia", etiqueta: "Tipografía", listo: datos.tieneTipografia, ir: () => onAgregar("tipografia") },
    { clave: "memoria", etiqueta: "Memoria de la IA", listo: datos.tieneMemoria, ir: () => onAgregar("memoria") },
    { clave: "productos", etiqueta: "Tu primer producto", listo: datos.productos > 0, ir: () => onAgregar("producto") },
    { clave: "modelos", etiqueta: `Tu primer ${palabraModelo}`, listo: datos.modelos > 0, ir: () => onAgregar("modelo") },
  ];

  // Halo: tres manchas de luz con los colores de la marca, que se apagan hacia abajo —
  // ADR-2026-10-02-mi-marca-halo-de-marca. Sufijos de alfa en hex (~22%, 18%, 20%):
  // tiene que teñir, no competir con el logo ni con el texto.
  const [c1, c2, c3] = coloresDelHalo(paleta);
  const halo = [
    `radial-gradient(46% 100% at 4% 0%, ${c1}38 0%, transparent 70%)`,
    `radial-gradient(40% 85% at 42% 0%, ${c2}2e 0%, transparent 70%)`,
    `radial-gradient(44% 95% at 92% 0%, ${c3}33 0%, transparent 70%)`,
  ].join(", ");

  return (
    <div className={`relative isolate flex flex-col gap-3 lg:gap-5 ${menuAbierto ? "z-30" : ""}`}>
      {/* El halo sale 32px a los costados y arriba —la sangría del shell— y se
          desvanece con una máscara en vez de terminar en línea. Aparece suave al
          cambiar de marca (el `key`). */}
      <div
        key={paleta.join(",") || "sin-paleta"}
        aria-hidden
        className="pointer-events-none absolute -inset-x-4 top-0 -z-10 h-[260px] sm:-inset-x-8 lg:-top-8 lg:h-[300px] animate-[aparecer-suave_0.8s_ease-out_both]"
        style={{
          background: halo,
          // Dos máscaras intersectadas: se apaga hacia abajo Y hacia los costados. Sólo
          // con la vertical se veía el corte recto de los lados cuando la columna del
          // shell es más ancha que el contenido (pantallas anchas).
          WebkitMaskImage: escritorio ? MASCARA_DEL_HALO : MASCARA_DEL_HALO_MOBILE,
          maskImage: escritorio ? MASCARA_DEL_HALO : MASCARA_DEL_HALO_MOBILE,
          WebkitMaskComposite: "source-in",
          maskComposite: "intersect",
        }}
      />
      {/* Columnas: logo · nombre y datos (tan anchos como el texto, pueden achicarse) ·
          resto. La pill de "Completá tu marca" va en la tercera, a la derecha del
          nombre y centrada con el bloque nombre + datos: es más alta que el título y en
          su misma línea lo empujaba hacia abajo. */}
      {/* Abajo de `lg`: logo · nombre y datos, y la pill en un renglón propio a todo
          el ancho (al lado del nombre no entra en un teléfono). */}
      <div className="grid min-w-0 grid-cols-[56px_minmax(0,1fr)] items-center gap-x-3.5 lg:grid-cols-[72px_minmax(0,max-content)_minmax(0,1fr)] lg:gap-x-5">
        <div className="relative col-start-1 row-span-2 row-start-1 h-14 w-14 overflow-hidden rounded-2xl bg-white shadow-[0_8px_24px_-10px_rgba(16,24,40,0.25),0_0_0_1px_rgba(16,24,40,0.06)] lg:h-[72px] lg:w-[72px] lg:rounded-[20px]">
          {logoUrl && !logoRoto ? (
            <img
              src={logoUrl}
              alt={`Logo de ${nombre}`}
              onError={() => setLogoRoto(true)}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div
              className="absolute inset-0 flex items-center justify-center text-[19px] lg:text-[24px] font-bold tracking-[-0.02em] text-white"
              style={{ background: paleta[0] ?? "#16181D" }}
            >
              {iniciales(nombre)}
            </div>
          )}
        </div>

        {escritorio ? (
          <h1 className="col-start-2 row-start-1 self-end truncate">{nombre}</h1>
        ) : onCambiarMarca ? (
          <h2 className="col-start-2 row-start-1 self-end min-w-0">
            <button
              type="button"
              onClick={onCambiarMarca}
              aria-haspopup="dialog"
              className="flex max-w-full items-center gap-1 text-left text-[21px] font-semibold leading-tight tracking-[-0.01em] text-brand-black transition-opacity active:opacity-70"
            >
              <span className="truncate">{nombre}</span>
              {/* El mismo glifo que el selector de marca y la tarjeta de la sidebar. */}
              <span className="material-symbols-rounded shrink-0 text-[20px] text-black/40">
                unfold_more
              </span>
            </button>
          </h2>
        ) : (
          <h2 className="col-start-2 row-start-1 self-end truncate text-[21px] font-semibold leading-tight tracking-[-0.01em] text-brand-black">
            {nombre}
          </h2>
        )}
        <div className="col-start-2 row-start-2 mt-1 lg:mt-1.5 self-start flex flex-wrap items-center gap-x-2.5 gap-y-1 lg:gap-x-3 lg:gap-y-1.5 text-[13px] lg:text-[13.5px] text-black/50">
          {rubro && <span>{rubro}</span>}
          {paleta.length > 0 && (
            <>
              {rubro && <span aria-hidden className="h-3.5 w-px bg-black/[0.12]" />}
              <span className="flex items-center" title="Paleta de la marca">
                {paleta.slice(0, 6).map((hex, i) => (
                  <span
                    key={`${hex}-${i}`}
                    // Borde interno además del aro: un color claro (blanco, crema)
                    // se perdía contra el fondo de la página.
                    className="-ml-1 h-4 w-4 lg:h-[18px] lg:w-[18px] rounded-full ring-2 ring-[#f9f9f9] first:ml-0"
                    style={{ background: hex, boxShadow: "inset 0 0 0 1px rgba(16,24,40,0.12)" }}
                  />
                ))}
              </span>
            </>
          )}
          <span aria-hidden className="h-3.5 w-px bg-black/[0.12]" />
          <button
            type="button"
            onClick={onEditar}
            className="inline-flex items-center gap-1.5 font-medium text-black/60 transition-colors hover:text-[#16181D]"
          >
            <PenLine className="h-3.5 w-3.5" />
            {/* "Información" sobra en un teléfono: el renglón es corto y el tab se
                llama así. */}
            Editar<span className="hidden lg:inline"> información</span>
          </button>
        </div>
        {/* Completa, en mobile no va: "Marca completa" no pide nada y ahí cada renglón
            cuenta — ADR-2026-10-05-mi-marca-completa-no-va-en-mobile. */}
        {pasos && (
          <div className={`${pasos.every((p) => p.listo) ? "hidden lg:block " : ""}col-span-2 row-start-3 mt-4 lg:col-span-1 lg:col-start-3 lg:row-span-2 lg:row-start-1 lg:mt-0 lg:mb-1 lg:ml-3 lg:self-end lg:justify-self-start`}>
            <CompletitudDeMarca pasos={pasos} onAbiertoChange={setMenuAbierto} />
          </div>
        )}
      </div>

      {/* Las integraciones en su propia fila, siempre: al lado del nombre caían
          abajo o arriba según el ancho, y la cabecera cambiaba de forma sola. */}
      {/* Abajo de `lg` las conexiones son un solo renglón que abre una hoja; en
          escritorio, los chips de siempre — ADR-2026-10-05-mi-marca-conexiones-en-un-renglon. */}
      {children && <ConexionesDeMarca>{children}</ConexionesDeMarca>}
    </div>
  );
};
